/**
 * PNG to SVG Converter Component
 */

import { memo, useState, useCallback } from 'react';
import { useToast } from '../../context';
import { convertPngToSvg, type ConversionMode, type TracePreset } from '../../services/pngToSvgService';
import { MESSAGES } from '../../config/constants';
import { analytics } from '../../services/analyticsService';

// Icons
const UploadIcon = memo(() => (
  <svg className="w-12 h-12 mx-auto mb-4 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
));
UploadIcon.displayName = 'UploadIcon';

const DownloadIcon = memo(() => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
));
DownloadIcon.displayName = 'DownloadIcon';

const CopyIcon = memo(() => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
));
CopyIcon.displayName = 'CopyIcon';

const SpinnerIcon = memo(() => (
  <svg className="w-8 h-8 animate-spin text-indigo-400" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
  </svg>
));
SpinnerIcon.displayName = 'SpinnerIcon';

export const PngToSvgConverter = memo(function PngToSvgConverter() {
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [svgResult, setSvgResult] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string>('converted.svg');
  const [conversionMode, setConversionMode] = useState<ConversionMode>('embed');
  const [tracePreset, setTracePreset] = useState<TracePreset>('default');
  const [colorCount, setColorCount] = useState<number>(16);
  const { success, error, info } = useToast();

  const processFile = useCallback(async (file: File) => {
    // Validate file type
    if (!file.type.startsWith('image/') || file.type === 'image/svg+xml') {
      error('Please upload a PNG, JPG, or other raster image file.');
      return;
    }

    setIsProcessing(true);
    info('Converting image to SVG...');

    try {
      // Store original image for preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
      };
      reader.readAsDataURL(file);

      // Convert to SVG with selected mode
      const svg = await convertPngToSvg(file, { 
        mode: conversionMode, 
        preset: tracePreset,
        colors: colorCount 
      });
      setSvgResult(svg);
      setFileName(file.name.replace(/\.[^/.]+$/, '.svg'));
      success('Image converted to SVG successfully!');
      analytics.pngToSvg.convert(file.type);
    } catch (err) {
      error(MESSAGES.errors.conversionFailed);
      console.error('Conversion error:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [success, error, info, conversionMode, tracePreset, colorCount]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  }, [processFile]);

  const handleDownload = useCallback(() => {
    if (!svgResult) return;
    
    const blob = new Blob([svgResult], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
    success(MESSAGES.success.downloaded);
    analytics.pngToSvg.download();
  }, [svgResult, fileName, success]);

  const handleCopy = useCallback(async () => {
    if (!svgResult) return;
    
    try {
      await navigator.clipboard.writeText(svgResult);
      success(MESSAGES.success.copied);
    } catch {
      error(MESSAGES.errors.clipboardFailed);
    }
  }, [svgResult, success, error]);

  const handleClear = useCallback(() => {
    setOriginalImage(null);
    setSvgResult(null);
  }, []);

  return (
    <div className="space-y-6">
      {/* Conversion Mode Selector */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
        <h3 className="text-sm font-medium text-slate-300 mb-3">Conversion Mode</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <button
            onClick={() => setConversionMode('embed')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              conversionMode === 'embed'
                ? 'border-indigo-500 bg-indigo-500/10'
                : 'border-slate-600 hover:border-slate-500 bg-slate-700/50'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">📦</span>
              <span className="font-medium text-white text-sm sm:text-base">Embed (Recommended)</span>
            </div>
            <p className="text-xs text-slate-400">
              Embeds image as base64 inside SVG. Perfect quality, works with any image.
            </p>
          </button>
          <button
            onClick={() => setConversionMode('trace')}
            className={`p-4 rounded-lg border-2 transition-all text-left ${
              conversionMode === 'trace'
                ? 'border-indigo-500 bg-indigo-500/10'
                : 'border-slate-600 hover:border-slate-500 bg-slate-700/50'
            }`}
          >
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg">✏️</span>
              <span className="font-medium text-white text-sm sm:text-base">Trace (Vector)</span>
            </div>
            <p className="text-xs text-slate-400">
              Creates true vector paths using imagetracerjs. Best for logos/icons.
            </p>
          </button>
        </div>
      </div>

      {/* Trace Options - Only show when trace mode selected */}
      {conversionMode === 'trace' && (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <h3 className="text-sm font-medium text-slate-300 mb-3">Trace Settings</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">Preset Style</label>
              <select
                value={tracePreset}
                onChange={(e) => setTracePreset(e.target.value as TracePreset)}
                className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white focus:border-indigo-500 focus:outline-none"
              >
                <option value="default">Default</option>
                <option value="posterized">Posterized</option>
                <option value="curvy">Curvy</option>
                <option value="sharp">Sharp</option>
                <option value="detailed">Detailed</option>
                <option value="smoothed">Smoothed</option>
                <option value="grayscale">Grayscale</option>
                <option value="artistic">Artistic</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1.5">Number of Colors: {colorCount}</label>
              <input
                type="range"
                min="2"
                max="64"
                value={colorCount}
                onChange={(e) => setColorCount(Number(e.target.value))}
                className="w-full accent-indigo-500"
              />
              <div className="flex justify-between text-xs text-slate-500 mt-1">
                <span>2</span>
                <span>64</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Drop Zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative rounded-xl border-2 border-dashed p-8 text-center transition-all cursor-pointer
          ${isDragging 
            ? 'border-indigo-500 bg-indigo-500/10' 
            : 'border-slate-600 hover:border-indigo-400 bg-slate-800/50'
          }
          ${isProcessing ? 'pointer-events-none opacity-50' : ''}
        `}
      >
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
          onChange={handleFileInput}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          disabled={isProcessing}
        />
        
        {isProcessing ? (
          <div className="py-4">
            <SpinnerIcon />
            <p className="mt-4 text-slate-400">Converting image to SVG...</p>
          </div>
        ) : (
          <>
            <UploadIcon />
            <p className="text-slate-300 font-medium">
              Drop your PNG/JPG image here
            </p>
            <p className="text-slate-500 text-sm mt-1">
              or click to browse files
            </p>
          </>
        )}
      </div>

      {/* Results */}
      {svgResult && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Original Image */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <h3 className="text-sm font-medium text-slate-400 mb-3">Original Image</h3>
            <div className="bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMzMzIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMzMzMiLz48L3N2Zz4=')] rounded-lg p-4 flex items-center justify-center min-h-[200px]">
              {originalImage && (
                <img 
                  src={originalImage} 
                  alt="Original" 
                  className="max-w-full max-h-[300px] object-contain"
                />
              )}
            </div>
          </div>

          {/* SVG Result */}
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-slate-400">Converted SVG</h3>
              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-700 hover:bg-slate-600 rounded-lg transition-colors"
                >
                  <CopyIcon />
                  <span>Copy</span>
                </button>
                <button
                  onClick={handleDownload}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors"
                >
                  <DownloadIcon />
                  <span>Download</span>
                </button>
              </div>
            </div>
            <div className="bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCI+PHJlY3Qgd2lkdGg9IjEwIiBoZWlnaHQ9IjEwIiBmaWxsPSIjMzMzIi8+PHJlY3QgeD0iMTAiIHk9IjEwIiB3aWR0aD0iMTAiIGhlaWdodD0iMTAiIGZpbGw9IiMzMzMiLz48L3N2Zz4=')] rounded-lg p-4 flex items-center justify-center min-h-[200px]">
              <div 
                dangerouslySetInnerHTML={{ __html: svgResult }}
                className="max-w-full max-h-[300px] [&>svg]:max-w-full [&>svg]:max-h-[300px]"
              />
            </div>
          </div>
        </div>
      )}

      {/* Clear Button */}
      {svgResult && (
        <div className="text-center">
          <button
            onClick={handleClear}
            className="px-6 py-2.5 text-sm font-medium bg-slate-700 hover:bg-slate-600 text-white rounded-lg border border-slate-600 hover:border-slate-500 transition-all flex items-center gap-2 mx-auto"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Convert Another Image
          </button>
        </div>
      )}

      {/* Info */}
      <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
        <h4 className="text-sm font-medium text-slate-300 mb-2">
          {conversionMode === 'embed' ? '📦 Embed Mode Info:' : '✏️ Trace Mode Tips:'}
        </h4>
        {conversionMode === 'embed' ? (
          <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
            <li>Image is embedded as base64 data - perfect quality preservation</li>
            <li>Works with any image: photos, gradients, complex graphics</li>
            <li>Output is an SVG file that contains the raster image</li>
            <li>Great for when you need an SVG container for any image</li>
          </ul>
        ) : (
          <ul className="text-sm text-slate-400 space-y-1 list-disc list-inside">
            <li>Uses imagetracerjs library for professional vectorization</li>
            <li>Try different presets: "Posterized" for flat colors, "Curvy" for smooth shapes</li>
            <li>Adjust color count - fewer colors = simpler/smaller SVG</li>
            <li>Best results with logos, icons, and simple graphics</li>
          </ul>
        )}
      </div>
    </div>
  );
});

export default PngToSvgConverter;
